import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as LaboratoryPersonnelActions from "../../../actions/laboratoryPersonnels";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import { DatePickerFormInputFabric, SelectFormInput } from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { LaboratoryPersonnelBreadcrumbs, LaboratoryPersonnelCreateBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { LaboratoryPersonnelSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ILaboratoryPersonnelReduxFormState } from "../../../reducers/reactReduxForms/laboratoryPersonnel";

import "./LabPersonnelPage.scss";

interface LabPersonnelPageProps {
    form: Dtos.LaboratoryPersonnel & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ILaboratoryPersonnelReduxFormState;
    laboratoryPersonnel: Dtos.LaboratoryPersonnel;
    lookups: Dtos.Lookup[];
    loadingLaboratoryPersonnel: boolean;
    loadLaboratoryPersonnelSuccess: boolean;
    loadLaboratoryPersonnelFailure: boolean;
    loadLaboratoryPersonnelErrorCode: string;
    savingLaboratoryPersonnel: boolean;
    saveLaboratoryPersonnelSuccess: boolean;
    saveLaboratoryPersonnelFailure: boolean;
    creatingLaboratoryPersonnel: boolean;
    createLaboratoryPersonnelSuccess: boolean;
    createLaboratoryPersonnelFailure: boolean;
    validatingLaboratoryPersonnel: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearLaboratoryPersonnel: LaboratoryPersonnelActions.ILaboratoryPersonnelClearActionCreator;
    loadLaboratoryPersonnel: LaboratoryPersonnelActions.ILaboratoryPersonnelLoadByIdActionCreator;
    saveLaboratoryPersonnel: LaboratoryPersonnelActions.ILaboratoryPersonnelSaveActionCreator;
    createLaboratoryPersonnel: LaboratoryPersonnelActions.ILaboratoryPersonnelCreateActionCreator;
    validateLaboratoryPersonnel: LaboratoryPersonnelActions.ILaboratoryPersonnelValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.laboratoryPersonnel";

const LabPersonnelPage: FC<LabPersonnelPageProps> = ({
    form,
    formState,
    formProperties,
    laboratoryPersonnel,
    lookups,
    loadingLaboratoryPersonnel,
    loadLaboratoryPersonnelFailure,
    loadLaboratoryPersonnelErrorCode,
    savingLaboratoryPersonnel,
    saveLaboratoryPersonnelSuccess,
    saveLaboratoryPersonnelFailure,
    creatingLaboratoryPersonnel,
    validatingLaboratoryPersonnel,
    validationFailures,
    permissions,
    clearLaboratoryPersonnel,
    loadLaboratoryPersonnel,
    saveLaboratoryPersonnel,
    createLaboratoryPersonnel,
    validateLaboratoryPersonnel,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string; }>();
    const { laboratoryPersonnelId } = useParams<{ laboratoryPersonnelId: string; }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);
    const laboratoryPersonnelIdNumber = parseInt(laboratoryPersonnelId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Laboratory Personnel
    useEffect(() => {
        if (
            laboratoryIdNumber &&
            laboratoryPersonnelIdNumber &&
            !laboratoryPersonnel
        ) {
            setDrawerItems(
                LaboratoryPersonnelSubMenu(
                    laboratoryIdNumber,
                    laboratoryPersonnelIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                LaboratoryPersonnelBreadcrumbs(
                    laboratoryIdNumber,
                    laboratoryPersonnelIdNumber
                )
            );

            loadLaboratoryPersonnel(laboratoryPersonnelIdNumber);
        } else if (
            laboratoryIdNumber &&
            !laboratoryPersonnelIdNumber &&
            !laboratoryPersonnel
        ) {
            setBreadcrumbs(
                LaboratoryPersonnelCreateBreadcrumbs(laboratoryIdNumber)
            );
            setDrawerItems([]);
            createLaboratoryPersonnel(laboratoryIdNumber);
        } else if (laboratoryPersonnel && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        laboratoryPersonnelIdNumber,
        laboratoryPersonnel,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadLaboratoryPersonnel,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoryPersonnelFailure) {
            navigate(`/error/${loadLaboratoryPersonnelErrorCode}`);
        }
    }, [
        loadLaboratoryPersonnelFailure,
        loadLaboratoryPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            laboratoryIdNumber &&
            laboratoryPersonnelIdNumber &&
            laboratoryPersonnel?.laboratory &&
            laboratoryPersonnel?.personnel
        ) {
            setBreadcrumbs(
                LaboratoryPersonnelBreadcrumbs(
                    laboratoryIdNumber,
                    laboratoryPersonnelIdNumber,
                    laboratoryPersonnel.laboratory?.name,
                    laboratoryPersonnel.personnel?.displayWithSalutation
                )
            );
        }
    }, [
        laboratoryIdNumber,
        laboratoryPersonnel,
        laboratoryPersonnelIdNumber,
        setDrawerItems,
        setBreadcrumbs,
        LaboratoryPersonnelBreadcrumbs,
    ]);

    // Reload laboratory personnel after saving
    useEffect(() => {
        if (!savingLaboratoryPersonnel && saveLaboratoryPersonnelSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Laboratory personnel saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/labs/${laboratoryIdNumber}/personnel`);
            } else if (!laboratoryPersonnelIdNumber) {
                navigate(
                    `/labs/${laboratoryIdNumber}/personnel/${laboratoryPersonnel?.id}/details`
                );
                clearLaboratoryPersonnel();
            } else {
                loadLaboratoryPersonnel(laboratoryPersonnelIdNumber);
            }
        }
    }, [
        savingLaboratoryPersonnel,
        saveLaboratoryPersonnelSuccess,
        loadLaboratoryPersonnel,
        laboratoryPersonnelIdNumber,
        navigate,
        saveAndReturn,
        clearLaboratoryPersonnel,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingLaboratoryPersonnel && saveLaboratoryPersonnelFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingLaboratoryPersonnel,
        saveLaboratoryPersonnelSuccess,
        loadLaboratoryPersonnel,
        laboratoryPersonnelIdNumber,
    ]);

    // Cleanup effect to clear the laboratory personnel on component unmount
    useEffect(() => {
        return () => {
            clearLaboratoryPersonnel();
            resetForm(reduxFormName);
        };
    }, [clearLaboratoryPersonnel, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.LaboratoryPersonnel =
                {} as Dtos.LaboratoryPersonnel;

            if (!data.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            await saveLaboratoryPersonnel(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.LaboratoryPersonnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/labs/${laboratoryIdNumber}/personnel`);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.LaboratoryPersonnel =
            {} as Dtos.LaboratoryPersonnel;

        loadForm(
            reduxFormName,
            Object.assign({ ...laboratoryPersonnel }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.LaboratoryPersonnel =
                {} as Dtos.LaboratoryPersonnel;

            if (!form.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            if (form && hasBeenSubmitted && !validatingLaboratoryPersonnel) {
                await validateLaboratoryPersonnel(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                laboratoryPersonnelIdNumber,
                permissions,
                Dtos.Permission.LabPersonnelUpdate,
                Dtos.Permission.LabPersonnelCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingLaboratoryPersonnel ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            personnelDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Personnel Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "laboratoryPersonnel",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-1",
                                                fieldClassName: "col-sm-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            roleDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Role Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "laboratoryPersonnel",
                                        {
                                            roleId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "LaboratoryPersonnel",
                                                                "roleId",
                                                                lookups
                                                            ),
                                                            form.roleId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-1",
                                                fieldClassName:
                                                    "col-sm-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryPersonnel",
                                        {
                                            dateStarted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryPersonnel",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.personnelId,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "laboratoryPersonnel",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        laboratoryPersonnelIdNumber,
        permissions,
        savingLaboratoryPersonnel,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        personnelSearchLoader
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabPersonnelView}
            loading={
                loadingLaboratoryPersonnel ||
                creatingLaboratoryPersonnel ||
                savingLaboratoryPersonnel
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<LabPersonnelPageProps> => {
    let laboratoryPersonnel: Dtos.LaboratoryPersonnel | undefined = !(
        state.laboratoryPersonnels.formData instanceof Array
    )
        ? state.laboratoryPersonnels.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratoryPersonnel: laboratoryPersonnel!,
        loadingLaboratoryPersonnel:
            state.laboratoryPersonnels.loadState &&
            state.laboratoryPersonnels.loadState.status == RequestState.Pending,
        loadLaboratoryPersonnelSuccess:
            state.laboratoryPersonnels.loadState &&
            state.laboratoryPersonnels.loadState.status == RequestState.Success,
        loadLaboratoryPersonnelFailure:
            state.laboratoryPersonnels.loadState &&
            state.laboratoryPersonnels.loadState.status == RequestState.Failure,
        loadLaboratoryPersonnelErrorCode:
            state.laboratoryPersonnels.loadState.errorCode,
        savingLaboratoryPersonnel:
            state.laboratoryPersonnels.saveState &&
            state.laboratoryPersonnels.saveState.status == RequestState.Pending,
        saveLaboratoryPersonnelSuccess:
            state.laboratoryPersonnels.saveState &&
            state.laboratoryPersonnels.saveState.status == RequestState.Success,
        saveLaboratoryPersonnelFailure:
            state.laboratoryPersonnels.saveState &&
            state.laboratoryPersonnels.saveState.status == RequestState.Failure,
        creatingLaboratoryPersonnel:
            state.laboratoryPersonnels.createState &&
            state.laboratoryPersonnels.createState.status ==
                RequestState.Pending,
        createLaboratoryPersonnelSuccess:
            state.laboratoryPersonnels.createState &&
            state.laboratoryPersonnels.createState.status ==
                RequestState.Success,
        createLaboratoryPersonnelFailure:
            state.laboratoryPersonnels.createState &&
            state.laboratoryPersonnels.createState.status ==
                RequestState.Failure,
        validatingLaboratoryPersonnel:
            state.laboratoryPersonnels.validateState &&
            state.laboratoryPersonnels.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.laboratoryPersonnels.lookups,
        form: state.reduxForms.laboratoryPersonnel,
        formState: state.laboratoryPersonnels.formState,
        formProperties: state.laboratoryPersonnels.formProperties,
        reduxFormState: state.reduxForms.formState.laboratoryPersonnel,
        validationFailures: laboratoryPersonnel
            ? state.laboratoryPersonnels.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<LabPersonnelPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadLaboratoryPersonnel: bindActionCreators(
            LaboratoryPersonnelActions.LoadLaboratoryPersonnelById,
            dispatch
        ),
        clearLaboratoryPersonnel: bindActionCreators(
            LaboratoryPersonnelActions.Clear,
            dispatch
        ),
        saveLaboratoryPersonnel: bindActionCreators(
            LaboratoryPersonnelActions.SaveLaboratoryPersonnel,
            dispatch
        ),
        createLaboratoryPersonnel: bindActionCreators(
            LaboratoryPersonnelActions.CreateLaboratoryPersonnel,
            dispatch
        ),
        validateLaboratoryPersonnel: bindActionCreators(
            LaboratoryPersonnelActions.ValidateLaboratoryPersonnel,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LabPersonnelPage);
